import { useCallback, useEffect, useRef, useState } from "react";
import type { UIEvent } from "react";

export interface SearchPage<T> {
  rows: T[];
  total: number;
}

interface UsePaginatedSearchOptions<T> {
  /** Fetches only while open; closing clears the search and the rows. */
  open: boolean;
  /** One page from `offset`; `search` is trimmed, "" means no filter. */
  fetchPage: (search: string, offset: number) => Promise<SearchPage<T>>;
  /** Changing it restarts from the first page (a new sort, another previewed account). */
  resetKey?: string;
}

interface UsePaginatedSearchResult<T> {
  searchValue: string;
  setSearchValue: (v: string) => void;
  items: T[];
  /** Rows matching the search on the server, loaded or not. */
  total: number;
  loading: boolean;
  /** The last page failed; paging stops until the next reset. */
  failed: boolean;
  loadMore: () => void;
  /** For a scrolling list: loads the next page 80 px before the bottom. */
  onScroll: (event: UIEvent<HTMLElement>) => void;
  /** Edits loaded rows in place. */
  updateItems: (update: (item: T) => T) => void;
  /** Drops rows deleted on the server, so the next page does not skip as many. */
  removeItems: (predicate: (item: T) => boolean) => void;
}

/** A server-side searched list, paged in as the user scrolls. */
export function usePaginatedSearch<T>({
  open,
  fetchPage,
  resetKey,
}: UsePaginatedSearchOptions<T>): UsePaginatedSearchResult<T> {
  const [searchValue, setSearchValue] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(open);
  const [failed, setFailed] = useState(false);

  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  // Bumped on every reset: a page answering for an older search is dropped, never appended.
  const generationRef = useRef(0);
  // Refs, not state: scroll events fire faster than a render propagates.
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const searchRef = useRef("");

  const load = useCallback((generation: number, offset: number) => {
    loadingRef.current = true;
    setLoading(true);
    fetchRef
      .current(searchRef.current, offset)
      .then((page) => {
        if (generation !== generationRef.current) return;
        setItems((prev) =>
          offset === 0 ? page.rows : [...prev, ...page.rows],
        );
        offsetRef.current = offset + page.rows.length;
        // An empty page ends the scroll, whatever the total claims.
        setTotal(page.rows.length > 0 ? page.total : offsetRef.current);
      })
      .catch(() => {
        if (generation !== generationRef.current) return;
        setTotal(offsetRef.current);
        setFailed(true);
      })
      .finally(() => {
        if (generation !== generationRef.current) return;
        loadingRef.current = false;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const generation = ++generationRef.current;
    offsetRef.current = 0;
    setItems([]);
    setTotal(0);
    setFailed(false);
    // Loading through the debounce too, so the list never flashes "no result".
    loadingRef.current = open;
    setLoading(open);
    if (!open) {
      setSearchValue("");
      return;
    }
    const search = searchValue.trim();
    const timer = setTimeout(
      () => {
        searchRef.current = search;
        load(generation, 0);
      },
      search ? 300 : 0,
    );
    return () => clearTimeout(timer);
  }, [open, searchValue, resetKey, load]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || offsetRef.current >= total) return;
    load(generationRef.current, offsetRef.current);
  }, [load, total]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop <= el.clientHeight + 80) loadMore();
    },
    [loadMore],
  );

  const updateItems = useCallback((update: (item: T) => T) => {
    setItems((prev) => prev.map(update));
  }, []);

  const removeItems = useCallback(
    (predicate: (item: T) => boolean) => {
      const removed = items.filter(predicate).length;
      if (removed === 0) return;
      setItems((prev) => prev.filter((item) => !predicate(item)));
      offsetRef.current -= removed;
      setTotal((prev) => prev - removed);
    },
    [items],
  );

  return {
    searchValue,
    setSearchValue,
    items,
    total,
    loading,
    failed,
    loadMore,
    onScroll,
    updateItems,
    removeItems,
  };
}
